import moment from "moment";

export const FEATURES = {
  ADD_CUSTOMERS: "ADD_CUSTOMERS",
  CHECKOUT_TIMER: "CHECKOUT_TIMER",
  PREMIUM_TEMPLATES: "PREMIUM_TEMPLATES",
  COMMUNITY: "COMMUNITY",
  STORAGE_LIMIT: "STORAGE_LIMIT",
  SPARKS: "SPARKS",
  WEBINAR: "WEBINAR",
  COURSE_RECOMMENDATION: "COURSE_RECOMMENDATION",
  FLEXI_PAYMENT: "FLEXI_PAYMENT",
  INSTANT_WITHDRAWAL: "INSTANT_WITHDRAWAL",
  MANGO_AI: "MANGO_AI",
  THEME_AI: "THEME_AI",
  APP_BUILDER_AI: "APP_BUILDER_AI",
  WEBSITE_BUILDER_AI: "WEBSITE_BUILDER_AI",
  BANNER_AI: "BANNER_AI",
  MANAGE_SECRET: "MANAGE_SECRET",
  ENABLE_TEMPLATE: "ENABLE_TEMPLATE",
};

// Features that can be managed through the UI (excludes COMMUNITY)
export const MANAGEABLE_FEATURES = {
  ADD_CUSTOMERS: "ADD_CUSTOMERS",
  CHECKOUT_TIMER: "CHECKOUT_TIMER",
  PREMIUM_TEMPLATES: "PREMIUM_TEMPLATES",
  STORAGE_LIMIT: "STORAGE_LIMIT",
  SPARKS: "SPARKS",
  WEBINAR: "WEBINAR",
  COURSE_RECOMMENDATION: "COURSE_RECOMMENDATION",
  FLEXI_PAYMENT: "FLEXI_PAYMENT",
  INSTANT_WITHDRAWAL: "INSTANT_WITHDRAWAL",
  MANGO_AI: "MANGO_AI",
  THEME_AI: "THEME_AI",
  APP_BUILDER_AI: "APP_BUILDER_AI",
  WEBSITE_BUILDER_AI: "WEBSITE_BUILDER_AI",
  BANNER_AI: "BANNER_AI",
  MANAGE_SECRET: "MANAGE_SECRET",
  ENABLE_TEMPLATE: "ENABLE_TEMPLATE",
};

export const FEATURE_LABELS = {
  [FEATURES.ADD_CUSTOMERS]: "Add Customers",
  [FEATURES.CHECKOUT_TIMER]: "Checkout Timer",
  [FEATURES.PREMIUM_TEMPLATES]: "Premium Templates",
  [FEATURES.COMMUNITY]: "Community",
  [FEATURES.STORAGE_LIMIT]: "Storage Limit",
  [FEATURES.SPARKS]: "Sparks",
  [FEATURES.WEBINAR]: "Webinar",
  [FEATURES.COURSE_RECOMMENDATION]: "Course Recommendation",
  [FEATURES.FLEXI_PAYMENT]: "Flexi Payment",
  [FEATURES.INSTANT_WITHDRAWAL]: "Instant Withdrawal",
  [FEATURES.MANGO_AI]: "Mango AI",
  [FEATURES.THEME_AI]: "Theme AI",
  [FEATURES.APP_BUILDER_AI]: "App Builder AI",
  [FEATURES.WEBSITE_BUILDER_AI]: "Website Builder AI",
  [FEATURES.BANNER_AI]: "Banner AI",
  [FEATURES.MANAGE_SECRET]: "Manage Secret",
  [FEATURES.ENABLE_TEMPLATE]: "Enable Template",
};

// The MANGO_AI column holds the whole AI group, so its header names the group.
// The feature keeps its own label on its row inside the cell.
export const FEATURE_COLUMN_LABELS = {
  [FEATURES.MANGO_AI]: "AI Features",
};

export const getFeatureColumnLabel = (feature) =>
  FEATURE_COLUMN_LABELS[feature] || FEATURE_LABELS[feature];

// A column has to be at least wide enough for its own header, or the label
// wraps onto two lines. Derived from the label rather than hardcoded so the two
// cannot drift apart: roughly 9px per character at the header's font size, plus
// cell padding and room for the filter icon. `contentWidth` is what the cell
// body needs, and whichever is larger wins.
export const getFeatureColumnWidth = (feature, contentWidth = 140) =>
  Math.max(contentWidth, getFeatureColumnLabel(feature).length * 9 + 60);

export const FEATURE_MODAL_TOOLTIP = {
  [FEATURES.WEBINAR]: "Configure Webinar Access for users",
  [FEATURES.MANGO_AI]:
    "Mango AI chat access, plus the creator's AI credit balance. Credits entered here are ADDED to the balance, not set — leave the box blank for no top-up.",
  [FEATURES.WEBSITE_BUILDER_AI]: "Gates the Funnel Builder for this creator",
  [FEATURES.MANAGE_SECRET]: "Secret management (admin-only surface)",
  [FEATURES.ENABLE_TEMPLATE]: "Template editing (admin-only surface)",
};

export const FEATURE_INCLUDED_TIERS = {
  [FEATURES.ADD_CUSTOMERS]: ["tier3", "tier4"],
  [FEATURES.CHECKOUT_TIMER]: ["tier2", "tier3", "tier4"],
  [FEATURES.PREMIUM_TEMPLATES]: ["tier2", "tier3", "tier4"],
  [FEATURES.WEBINAR]: ["tier3", "tier4"],
};

// Features that must carry an expiry whenever they are on, whatever the tier.
// FEATURE_INCLUDED_TIERS makes an expiry conditional on the plan; this is
// unconditional.
export const MANDATORY_EXPIRY_FEATURES = [FEATURES.APP_BUILDER_AI];

export const isExpiryMandatoryForFeature = (feature) =>
  MANDATORY_EXPIRY_FEATURES.includes(feature);

// Enabling a mandatory-expiry feature with nothing stored defaults to a year
// out, so the admin is never left to fill it in from scratch.
export const getDefaultExpiryAt = () =>
  moment().add(1, "year").endOf("day").toISOString();

export const PLATFORM_OPTIONS = [
  { value: "tm", label: "TagMango" },
  { value: "wl", label: "White Label" },
  { value: "both", label: "Both" },
];

export const PLATFORM_TITLE = {
  tm: "TagMango",
  wl: "White Label",
  both: "Both",
};

export const USER_TIER_OPTIONS = [
  { value: "tier1", label: "Basic" },
  { value: "tier2", label: "Pro" },
  { value: "tier3", label: "Advanced" },
  { value: "tier4", label: "Ultimate" },
];

export const PLAN_TYPE_OPTIONS = [
  { value: "freedom-plan", label: "Freedom Plan" },
  { value: "enterprise-plan", label: "Enterprise Plan" },
];

export const FEATURE_STATUS_OPTIONS = [
  { value: "enabled", label: "Enabled" },
  { value: "disabled", label: "Disabled" },
];

// MANGO_AI is now an ordinary feature — enabled/expiryAt/allowedUsers/source —
// that additionally carries the creator's AI credit balance. The seven AI
// switches that used to be booleans inside it are features of their own.
export const MANGO_AI_CREDIT_FIELD = "aicreditValue";

// The seven features the old MANGO_AI booleans became. They were all editable
// on default TagMango hosts before the split, because they were fields of
// MANGO_AI and that feature is exempt — so they stay exempt as a group rather
// than being silently re-restricted by becoming features of their own.
export const MANGO_AI_FEATURES = [
  FEATURES.MANGO_AI,
  FEATURES.THEME_AI,
  FEATURES.APP_BUILDER_AI,
  FEATURES.WEBSITE_BUILDER_AI,
  FEATURES.BANNER_AI,
  FEATURES.MANAGE_SECRET,
  FEATURES.ENABLE_TEMPLATE,
];

// aicreditValue is the lifetime balance. The monthly counters are a separate
// allowance, of which only what is left is worth showing — the admin tops up
// the lifetime balance, not the monthly one. Null when the API sent neither.
export const getMangoAiMonthlyAvailable = (mangoAiConfig) => {
  const allowance = mangoAiConfig?.monthlyCreditValue;
  const spent = mangoAiConfig?.monthlySpentValue;

  if (typeof allowance !== "number") return null;

  return Math.max(0, allowance - (typeof spent === "number" ? spent : 0));
};

// Credit values come back as consumption floats (2815.0821293832664), which
// wrap the cell and read as noise. Two decimals is plenty, and whole numbers
// stay whole.
export const formatAiCredits = (value) =>
  typeof value === "number" ? String(Math.round(value * 100) / 100) : null;

// Returned by the API and shown read-only. They are ignored on write, so they
// are stripped from the payload rather than round-tripped.
export const MANGO_AI_MONTHLY_FIELDS = [
  "monthlyCreditValue",
  "monthlySpentValue",
  "monthlyPeriod",
];

// Pre-migration responses still carry the old booleans inside MANGO_AI. They
// are no longer a supported write, so they are stripped before saving rather
// than echoed back.
export const LEGACY_MANGO_AI_FIELDS = [
  "mangoAi",
  "themeAi",
  "appBuilderAi",
  "websiteBuilderAi",
  "bannerAi",
  "manage_secret",
  "enable_template",
];

// The backend ignores ?MANGO_AI=true, so chat access is not filterable from the
// creator list even though the other AI features are.
export const UNFILTERABLE_FEATURES = [
  FEATURES.MANGO_AI,
  FEATURES.STORAGE_LIMIT,
];

// A feature is on only while it is enabled and unexpired; a missing feature, a
// false `enabled` or a past `expiryAt` all mean off, and a missing `expiryAt`
// means no end date. The backend applies the same rule.
export const isFeatureActive = (featureConfig) => {
  if (featureConfig?.enabled !== true) return false;

  return (
    !featureConfig.expiryAt || moment(featureConfig.expiryAt).isAfter(moment())
  );
};

// Default allowed users for features
export const DEFAULT_ALLOWED_USERS = [
  "creator_completed",
  "creator_restricted",
];

// Helper function to ensure feature has default allowedUsers if missing
export const ensureDefaultAllowedUsers = (featureConfig, featureKey) => {
  if (!featureConfig) return featureConfig;

  if (
    featureKey === MANAGEABLE_FEATURES.STORAGE_LIMIT ||
    featureKey === MANAGEABLE_FEATURES.COURSE_RECOMMENDATION
  ) {
    return featureConfig;
  }

  // Set default allowedUsers if missing or empty
  if (!featureConfig.allowedUsers || featureConfig.allowedUsers.length === 0) {
    return {
      ...featureConfig,
      allowedUsers: [...DEFAULT_ALLOWED_USERS],
    };
  }

  return featureConfig;
};

// Deep copy utility to avoid reference sharing
export const deepCopyFeatureConfig = (config) => {
  if (!config || typeof config !== "object") return config;
  if (Array.isArray(config))
    return config.map((item) => deepCopyFeatureConfig(item));

  const copied = {};
  Object.keys(config).forEach((key) => {
    copied[key] = deepCopyFeatureConfig(config[key]);
  });
  return copied;
};

// Helper function to get a deep copy of initial feature config
export const getInitialFeatureConfig = (featureKey) => {
  const config = INITIAL_FEATURE_CONFIG[featureKey];
  return config ? deepCopyFeatureConfig(config) : {};
};

// Helper function to get a deep copy of all initial feature configs
export const getAllInitialFeaturesConfig = () => {
  return deepCopyFeatureConfig(INITIAL_FEATURE_CONFIG);
};

// Helper function to process all features config with default allowedUsers
export const processFeatureConfigWithDefaults = (featuresConfig) => {
  if (!featuresConfig) return featuresConfig;

  const processedConfig = deepCopyFeatureConfig(featuresConfig);

  // Process all features (including COMMUNITY if it exists)
  Object.keys(processedConfig).forEach((featureKey) => {
    processedConfig[featureKey] = ensureDefaultAllowedUsers(
      processedConfig[featureKey],
      featureKey
    );
  });

  return processedConfig;
};

export const isExpiryRequiredForFeature = (feature, userTier) => {
  if (isExpiryMandatoryForFeature(feature)) return true;

  const includedTiers = FEATURE_INCLUDED_TIERS[feature];
  if (!includedTiers) return false;
  if (userTier && includedTiers.includes(userTier)) return false;
  return true;
};

export const validateExpiryRequirements = (users) => {
  const expiryFeatures = [
    ...new Set([
      ...Object.keys(FEATURE_INCLUDED_TIERS),
      ...MANDATORY_EXPIRY_FEATURES,
    ]),
  ];

  return users
    .filter((user) => user.status === "valid")
    .some((user) =>
      expiryFeatures.some((feature) => {
        const config = user.featuresConfig?.[feature];
        return (
          config?.enabled &&
          !config?.expiryAt &&
          isExpiryRequiredForFeature(feature, user.userTier)
        );
      })
    );
};

export const resolveFeatureSource = (
  feature,
  currentConfig,
  originalConfig
) => {
  if (!originalConfig || originalConfig.source !== "tier_feature") {
    return "admin_override";
  }

  if (feature === MANAGEABLE_FEATURES.STORAGE_LIMIT) {
    if (currentConfig?.storage !== originalConfig?.storage) {
      return "admin_override";
    }
  } else if (feature === MANAGEABLE_FEATURES.MANGO_AI) {
    // MANGO_AI is enabled/expiryAt like the rest, but a credit top-up is an
    // admin action too. The top-up box starts blank, so an absent value means
    // "no top-up" and must not detach a tier_feature grant from its tier when
    // an unrelated feature is saved.
    if (
      currentConfig?.enabled !== originalConfig?.enabled ||
      currentConfig?.expiryAt !== originalConfig?.expiryAt ||
      hasMangoAiCreditTopUp(currentConfig)
    ) {
      return "admin_override";
    }
  } else if (
    currentConfig?.enabled !== originalConfig?.enabled ||
    currentConfig?.expiryAt !== originalConfig?.expiryAt
  ) {
    return "admin_override";
  }

  return "tier_feature";
};

// A feature config is only worth saving when it actually carries a value.
export const hasConfiguredFeatureValues = (config) => {
  if (!config) return false;

  return (
    config.enabled !== undefined ||
    Boolean(config.platform) ||
    Boolean(config.expiryAt) ||
    Boolean(config.storage) ||
    config[MANGO_AI_CREDIT_FIELD] !== undefined
  );
};

// aicreditValue is the amount to ADD to the creator's balance, not the balance
// to set, so there is nothing to diff against the stored value: only a real
// top-up is worth sending. Zero, a cleared box and a negative number are all
// no-ops, and sending the balance shown on screen would add it a second time.
export const hasMangoAiCreditTopUp = (currentConfig) => {
  const credits = currentConfig?.[MANGO_AI_CREDIT_FIELD];

  return typeof credits === "number" && credits > 0;
};

// The top-up box always starts blank: the stored balance is shown beside it as
// read-only text, never as an editable value, because anything left in the box
// is added to the balance on save. The stored config stays in
// _originalFeaturesConfig so the balance can still be displayed.
export const clearMangoAiCreditValue = (featuresConfig) => {
  const mangoAiConfig = featuresConfig?.[MANAGEABLE_FEATURES.MANGO_AI];
  if (!mangoAiConfig) return featuresConfig;

  const { [MANGO_AI_CREDIT_FIELD]: stored, ...rest } = mangoAiConfig;

  return { ...featuresConfig, [MANAGEABLE_FEATURES.MANGO_AI]: rest };
};

// Drops aicreditValue from the MANGO_AI payload unless the admin entered a real
// top-up, so the key is absent rather than sent as null/0 and no balance is
// ever added by accident.
export const stripEmptyMangoAiCredit = (currentConfig) => {
  if (!currentConfig) return currentConfig;
  if (hasMangoAiCreditTopUp(currentConfig)) return currentConfig;

  const { [MANGO_AI_CREDIT_FIELD]: noTopUp, ...rest } = currentConfig;
  return rest;
};

// MANGO_AI is merged field by field on the server, so anything left in the
// object is written back. Drop the read-only monthly counters and the old
// booleans a pre-migration response may still carry.
export const stripUnwritableMangoAiFields = (currentConfig) => {
  if (!currentConfig) return currentConfig;

  const cleaned = { ...currentConfig };
  [...MANGO_AI_MONTHLY_FIELDS, ...LEGACY_MANGO_AI_FIELDS].forEach((field) => {
    delete cleaned[field];
  });

  return cleaned;
};

// Initial state for feature config (only manageable features)
export const INITIAL_FEATURE_CONFIG = {
  [MANAGEABLE_FEATURES.ADD_CUSTOMERS]: {
    platform: "wl",
    enabled: false,
    expiryAt: null,
    allowedUsers: ["creator_completed", "creator_restricted"],
  },
  [MANAGEABLE_FEATURES.CHECKOUT_TIMER]: {
    platform: "wl",
    enabled: false,
    expiryAt: null,
    allowedUsers: ["creator_completed", "creator_restricted"],
  },
  [MANAGEABLE_FEATURES.PREMIUM_TEMPLATES]: {
    platform: "wl",
    enabled: false,
    expiryAt: null,
    allowedUsers: ["creator_completed", "creator_restricted"],
  },
  [MANAGEABLE_FEATURES.STORAGE_LIMIT]: {
    storage: 20, // in GB
  },
  [MANAGEABLE_FEATURES.SPARKS]: {
    enabled: false,
  },
  [MANAGEABLE_FEATURES.COURSE_RECOMMENDATION]: {
    enabled: false,
    expiryAt: null,
  },
  [MANAGEABLE_FEATURES.FLEXI_PAYMENT]: {
    enabled: false,
    expiryAt: null,
  },
  [MANAGEABLE_FEATURES.INSTANT_WITHDRAWAL]: {
    enabled: false,
    expiryAt: null,
  },
  // aicreditValue is deliberately absent from MANGO_AI: the box is a top-up
  // amount, so an untouched row must not carry a default that would be added
  [MANAGEABLE_FEATURES.MANGO_AI]: {
    enabled: false,
    expiryAt: null,
    allowedUsers: ["creator_completed", "creator_restricted"],
  },
  [MANAGEABLE_FEATURES.THEME_AI]: {
    enabled: false,
    expiryAt: null,
    allowedUsers: ["creator_completed", "creator_restricted"],
  },
  [MANAGEABLE_FEATURES.APP_BUILDER_AI]: {
    enabled: false,
    expiryAt: null,
    allowedUsers: ["creator_completed", "creator_restricted"],
  },
  [MANAGEABLE_FEATURES.WEBSITE_BUILDER_AI]: {
    enabled: false,
    expiryAt: null,
    allowedUsers: ["creator_completed", "creator_restricted"],
  },
  [MANAGEABLE_FEATURES.BANNER_AI]: {
    enabled: false,
    expiryAt: null,
    allowedUsers: ["creator_completed", "creator_restricted"],
  },
  [MANAGEABLE_FEATURES.MANAGE_SECRET]: {
    enabled: false,
    expiryAt: null,
    allowedUsers: ["creator_completed", "creator_restricted"],
  },
  [MANAGEABLE_FEATURES.ENABLE_TEMPLATE]: {
    enabled: false,
    expiryAt: null,
    allowedUsers: ["creator_completed", "creator_restricted"],
  },
};

// Table filter types
export const TABLE_FILTER_TYPES = {
  SEARCH: "search",
  START_DATE: "startDate",
  END_DATE: "endDate",
  USER_TIER: "userTier",
  PLAN_TYPE: "planType",
  // Feature filter types
  ADD_CUSTOMERS: "ADD_CUSTOMERS",
  CHECKOUT_TIMER: "CHECKOUT_TIMER",
  PREMIUM_TEMPLATES: "PREMIUM_TEMPLATES",
  COMMUNITY: "COMMUNITY",
  STORAGE_LIMIT_FROM: "STORAGE_LIMIT_FROM",
  STORAGE_LIMIT_TO: "STORAGE_LIMIT_TO",
  SPARKS: "SPARKS",
  FLEXI_PAYMENT: "FLEXI_PAYMENT",
  INSTANT_WITHDRAWAL: "INSTANT_WITHDRAWAL",
};

// Initial filters state.
// Feature filter keys are generated dynamically from MANAGEABLE_FEATURES,
// so adding a new feature there automatically creates its filter entry here.
export const INITIAL_FILTERS = {
  search: "",
  startDate: null,
  endDate: null,
  userTier: null,
  planType: null,
  ...Object.values(MANAGEABLE_FEATURES).reduce((acc, feature) => {
    acc[feature] = null;
    return acc;
  }, {}),
  STORAGE_LIMIT_FROM: null,
  STORAGE_LIMIT_TO: null,
};

// Storage limit options (in GB)
export const STORAGE_LIMIT_OPTIONS = [
  { value: 1, label: "1 GB" },
  { value: 2, label: "2 GB" },
  { value: 5, label: "5 GB" },
  { value: 10, label: "10 GB" },
  { value: 20, label: "20 GB" },
  { value: 50, label: "50 GB" },
  { value: 100, label: "100 GB" },
  { value: 200, label: "200 GB" },
  { value: 500, label: "500 GB" },
  { value: 1000, label: "1 TB" },
  { value: "custom", label: "Custom" },
];

// Available custom apps for install/uninstall
export const CUSTOM_APPS = {
  LEVELUP: "levelup",
  UNIVERSE: "universe",
  CRM: "crm",
  FLUTE: "flute",
  SPARKS: "sparks",
  TM_AI_COFOUNDER: "tm-ai-cofounder",
};

export const CUSTOM_APP_OPTIONS = [
  { value: CUSTOM_APPS.LEVELUP, label: "LevelUp" },
  { value: CUSTOM_APPS.UNIVERSE, label: "Universe" },
  { value: CUSTOM_APPS.CRM, label: "CRM" },
  { value: CUSTOM_APPS.FLUTE, label: "Flute" },
  { value: CUSTOM_APPS.SPARKS, label: "Sparks" },
  { value: CUSTOM_APPS.TM_AI_COFOUNDER, label: "TM AI Cofounder" },
];

// Default hosts where most feature updates are blocked
export const DEFAULT_HOSTS = ["tagmango.com", "testing.tagmango.com"];

export const isDefaultHost = (host) =>
  DEFAULT_HOSTS.includes(host?.toLowerCase());

// Features that stay editable on default TagMango hosts. Storage is a quota
// rather than an access grant, and the Mango AI group is driven by the AI Verse
// add-on, which is per creator rather than per host — blocking it there would
// leave the parent switch unable to grant the capabilities it bundles.
export const HOST_UNRESTRICTED_FEATURES = [
  MANAGEABLE_FEATURES.STORAGE_LIMIT,
  ...MANGO_AI_FEATURES,
];

export const isFeatureHostRestricted = (feature, host) =>
  isDefaultHost(host) && !HOST_UNRESTRICTED_FEATURES.includes(feature);

// The host-level standard-withdrawal delay only holds back auto-approved
// payouts, which exist only while Instant Withdrawal is active. So the delay is
// editable for a host only when that feature is on and unexpired (TMG-3511) —
// an elapsed expiryAt produces no auto-approved payouts to delay.
export const isInstantWithdrawalActive = (featuresConfig) =>
  isFeatureActive(featuresConfig?.[MANAGEABLE_FEATURES.INSTANT_WITHDRAWAL]);

export const isWithdrawalDelayEditable = (user) =>
  !isDefaultHost(user?.host) && isInstantWithdrawalActive(user?.featuresConfig);

// Reverts pending delay edits on rows whose Instant Withdrawal is no longer
// active, so an input the admin can no longer see or edit never submits a
// value. Run this after any change that can flip the feature (including
// same-for-all propagation).
export const syncWithdrawalDelayWithInstantWithdrawal = (users) =>
  users.map((user) => {
    if (user.status !== "valid" || isWithdrawalDelayEditable(user)) return user;

    const originalDelay = user._originalStandardWithdrawalDelay ?? null;
    if (user.standardWithdrawalDelay === originalDelay) return user;

    return { ...user, standardWithdrawalDelay: originalDelay };
  });

// A delay is only worth sending when it is editable for the host and actually
// differs from the stored value, so unchanged rows never overwrite it.
export const hasWithdrawalDelayEdit = (user) =>
  isWithdrawalDelayEditable(user) &&
  typeof user.standardWithdrawalDelay === "number" &&
  user.standardWithdrawalDelay >= 0 &&
  user.standardWithdrawalDelay !== user._originalStandardWithdrawalDelay;

// AI Verse is an add-on rather than a feature flag: it is stored under
// addOnsConfig on the user document, not in featuresConfig, so it travels
// beside `email` and `host` in the payload instead of inside featuresConfig.
export const AI_VERSE_ADD_ON = "AIVERSE";

// addOnsConfig may be {}, AIVERSE may be absent and `enabled` may be undefined —
// none of it is back-filled for creators who never had the add-on touched, so
// compare with === true and read anything missing as off.
export const isAiVerseEnabled = (user) =>
  user?.addOnsConfig?.[AI_VERSE_ADD_ON]?.enabled === true;

// Omitting `aiVerse` leaves the add-on untouched, so it is only sent when the
// admin actually moved the switch away from the stored value. That is what
// keeps a CSV row — whose stored value the validation endpoint never returned —
// from switching the add-on off for everyone in the file. `_originalAiVerse` is
// null for those rows, so an untouched switch is never a boolean and never
// sent. The API rejects anything that is not a real boolean, hence the
// typeof check rather than a truthiness test.
export const hasAiVerseEdit = (user) =>
  typeof user?.aiVerse === "boolean" && user.aiVerse !== user._originalAiVerse;

// AI Verse bundles exactly these four features and moves in lockstep with them:
// switching the parent on grants all four, switching it off revokes all four,
// granting all four by hand switches the parent on, and taking any one of them
// away switches it off. The other AI features — App Builder AI, Manage Secret,
// Enable Template — and the credit balance are independent and never move it.
// Now that each capability is its own feature, the bundle is a list of feature
// keys rather than fields inside MANGO_AI.
export const AI_VERSE_BUNDLE_FEATURES = [
  FEATURES.MANGO_AI,
  FEATURES.THEME_AI,
  FEATURES.WEBSITE_BUILDER_AI,
  FEATURES.BANNER_AI,
];

// The seven AI features share a single column: MANGO_AI renders the whole
// group, with AI Verse as its parent, so the other six are not columns of
// their own. Splitting them into features changed the data model, not the
// screen.
export const MANGO_AI_INDEPENDENT_FEATURES = MANGO_AI_FEATURES.filter(
  (feature) => !AI_VERSE_BUNDLE_FEATURES.includes(feature)
);

export const MANGO_AI_GROUPED_FEATURES = MANGO_AI_FEATURES.filter(
  (feature) => feature !== FEATURES.MANGO_AI
);

// The features that get a column of their own, in order.
export const COLUMN_FEATURES = Object.values(MANAGEABLE_FEATURES).filter(
  (feature) => !MANGO_AI_GROUPED_FEATURES.includes(feature)
);

// The parent moves the whole bundle in both directions, so it yields a change
// per feature rather than a set of fields on one.
export const getAiVerseBundleChanges = (checked) =>
  AI_VERSE_BUNDLE_FEATURES.reduce(
    (acc, feature) => ({ ...acc, [feature]: checked }),
    {}
  );

// Lockstep follows the switch the admin sees, which is `enabled`, not the
// active-now rule that also weighs expiryAt.
export const isEveryAiVerseChildEnabled = (featuresConfig) =>
  AI_VERSE_BUNDLE_FEATURES.every(
    (feature) => featuresConfig?.[feature]?.enabled === true
  );

// The parent tracks the bundle exactly: on while all four are granted, off the
// moment any one of them is not. A change to a feature outside the bundle — or
// to anything other than a bundled feature's `enabled` — never moves it.
export const resolveAiVerseAfterChange = (
  currentAiVerse,
  featuresConfig,
  changedFeatures
) => {
  const touchesBundle = changedFeatures.some((feature) =>
    AI_VERSE_BUNDLE_FEATURES.includes(feature)
  );

  if (!touchesBundle) return currentAiVerse;

  return isEveryAiVerseChildEnabled(featuresConfig);
};

// App action types
export const APP_ACTIONS = {
  INSTALL: "install",
  UNINSTALL: "uninstall",
};

export const APP_ACTION_OPTIONS = [
  { value: APP_ACTIONS.INSTALL, label: "Install" },
  { value: APP_ACTIONS.UNINSTALL, label: "Uninstall" },
];
